import { useEffect, useRef } from 'react';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import { Stack, Container, Group, Divider, ScrollArea } from '@mantine/core';
import dealSchemaDefinition from '../../../../schemas/deal.schema';
import {
  useDealRepository,
  useDealStatusRepository,
  useDealTypeRepository,
} from '@ayocore/erp/crm/client/rpc';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';
import { UserSelect } from '@ayocore/platform/identity/client/components';
import type { CreateDeal, Deal } from '../../../../schemas/deal.schema';
import { PartnerSelect } from '@ayocore/erp/crm/client/components/Partners/PartnerSelect';
import { CRM_DEAL_STATUSES, CRM_DEAL_TYPES } from '@ayocore/erp/crm/constants';

/**
 * CreateDealForm - Deal creation form
 */
export function CreateDealForm({
  onDealCreated,
  initialData,
  disablePartner,
  initialSelectOptions,
}: {
  onDealCreated?: (deal?: Deal) => void;
  initialData?: Partial<CreateDeal>;
  /** Заблокировать смену партнёра (партнёр задан из внешнего контекста) */
  disablePartner?: boolean;
  /** Предзагруженные опции для RepositorySelect — чтобы показать label до загрузки полного списка */
  initialSelectOptions?: {
    partner_id?: { value: string; label: string };
    currency_id?: { value: string; label: string };
    deal_owner_id?: { value: string; label: string };
  };
}) {
  const dealRepository = useDealRepository();
  const dealStatusRepository = useDealStatusRepository();
  const dealTypeRepository = useDealTypeRepository();
  const currentUserId = useAuthController(s => s.user?.id);

  const SchemaForm = useSchemaForm<CreateDeal>(dealSchemaDefinition.createSchema, {
    initialValues: initialData || {},
    hideDescriptions: true,
  });

  // Флаг единоразового применения дефолтного ответственного. Иначе очистка крестиком
  // сразу перезаписывалась бы в этом же useEffect (deal_owner_id == null → currentUserId).
  const ownerDefaultApplied = useRef(false);

  useEffect(() => {
    const applyDefaults = async () => {
      const currentTypeId = SchemaForm.values.deal_type_id;
      let typeId = currentTypeId ?? null;

      if (!typeId) {
        const types = await dealTypeRepository.where('slug', CRM_DEAL_TYPES.SALE).limit(1).fetch();
        typeId = types[0]?.id ?? null;
        if (typeId) {
          SchemaForm.setFieldValue('deal_type_id', typeId);
        }
      }

      const currentStatusId = SchemaForm.values.deal_status_id;
      if (!currentStatusId && typeId) {
        const statusesBySlug = await dealStatusRepository
          .where('deal_type_id', typeId)
          .where('slug', CRM_DEAL_STATUSES.SALE_REQUEST)
          .limit(1)
          .fetch();

        const statusId =
          statusesBySlug[0]?.id ??
          (
            await dealStatusRepository
              .where('deal_type_id', typeId)
              .where('is_active', true)
              .orderBy('sort_order', 'asc')
              .limit(1)
              .fetch()
          )[0]?.id ??
          null;

        if (statusId) {
          SchemaForm.setFieldValue('deal_status_id', statusId);
        }
      }

      // По дефолту — текущий пользователь как ответственный. Только один раз:
      // после этого пользователь может очистить поле крестиком — не возвращаем.
      if (
        !ownerDefaultApplied.current &&
        SchemaForm.values.deal_owner_id == null &&
        currentUserId != null
      ) {
        SchemaForm.setFieldValue('deal_owner_id', currentUserId);
        ownerDefaultApplied.current = true;
      }
    };

    void applyDefaults();
  }, [SchemaForm, dealStatusRepository, dealTypeRepository, currentUserId]);

  const handleSubmit = async (values: CreateDeal) => {
    // Если будет ошибка — пробрасываем её дальше, чтобы useSchemaForm обработал её
    // (см. аналогичный паттерн в CreatePartnerForm). Свой try/catch с setError()
    // здесь не нужен — он глотает исключение, и wrapper useSchemaForm, не увидев
    // throw, следом показывает ещё и общий "Операция успешно выполнена".
    const newDeal = await dealRepository.create(values);
    SchemaForm.setSuccess(`Сделка "${newDeal.number ?? newDeal.id}" успешно создана!`);
    if (onDealCreated) {
      onDealCreated(newDeal as Deal);
    }

    return newDeal;
  };

  const handleCancel = () => {
    SchemaForm.reset();
    SchemaForm.clearMessages();
    if (onDealCreated) {
      onDealCreated();
    }
  };

  return (
    <form
      onSubmit={SchemaForm.handleSubmit(handleSubmit)}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
    >
      <ScrollArea.Autosize flex={1} mih={0} scrollbarSize={0}>
        <Container size='lg' px={{ base: 'xs', sm: 'sm' }} py='xs' maw='100%'>
          <Stack gap='xs' pb='md'>
            <SchemaForm.Messages />

            <PartnerSelect
              value={SchemaForm.values.partner_id?.toString() || ''}
              onChange={value =>
                SchemaForm.setFieldValue('partner_id', value ? parseInt(value) : null)
              }
              disabled={disablePartner}
              initialOption={initialSelectOptions?.partner_id}
            />

            <SchemaForm.Field.description />
            <Divider />

            <Group grow>
              <SchemaForm.Field.delivery_date_plan />
              <UserSelect
                placeholder='Ответственный...'
                label='Ответственный'
                value={SchemaForm.values.deal_owner_id}
                onChange={id => SchemaForm.setFieldValue('deal_owner_id', id)}
                initialOption={initialSelectOptions?.deal_owner_id}
              />
            </Group>
          </Stack>
        </Container>
      </ScrollArea.Autosize>

      <div
        style={{
          borderTop: '1px solid #e9ecef',
          backgroundColor: 'transparent',
          padding: '8px 0 0',
          flexShrink: 0,
        }}
      >
        <Group justify='flex-end'>
          <SchemaForm.buttons.cancel onClick={handleCancel} label='Отмена' size='xs' />
          <SchemaForm.buttons.submit label='Создать сделку' size='xs' />
        </Group>
      </div>
    </form>
  );
}
